import { useCallback } from 'react';
import { useQuery, NetworkStatus } from '@apollo/client';
import {
  GetInspectionInstancesQuery,
  GetInspectionInstancesQueryVariables,
  InspectionInstanceStatus,
} from 'graphql-query-contracts';
import { getInspectionInstancesQuery } from 'graphql-queries';

const DEFAULT_PER_PAGE = 20;

type PageInfo = { start: number; total: number; perPage: number };

const calculateHasNextPage = (pageInfo: PageInfo): boolean => {
  return pageInfo.start + pageInfo.perPage < pageInfo.total;
};

export interface UseGetInspectionInstancesParams {
  channelId: string;
  status?: InspectionInstanceStatus;
  assignedTo?: string;
  templateNameSearch?: string;
  perPage?: number;
  start?: number;
}

export interface UseGetInspectionInstancesResult {
  instances: NonNullable<
    GetInspectionInstancesQuery['inspectionInstances']
  >['inspectionInstances'];
  loading: boolean;
  error: ReturnType<typeof useQuery>['error'];
  refetch: (
    overrides?: Partial<UseGetInspectionInstancesParams>
  ) => Promise<void>;
  fetchMore: () => Promise<void>;
  hasNextPage: boolean;
  totalCount: number;
  pageInfo: NonNullable<
    GetInspectionInstancesQuery['inspectionInstances']
  >['pageInfo'];
  fetchingMore: boolean;
}

export function useGetInspectionInstances(
  params: UseGetInspectionInstancesParams
): UseGetInspectionInstancesResult {
  const {
    data,
    loading,
    error,
    refetch: apolloRefetch,
    fetchMore: apolloFetchMore,
    networkStatus,
  } = useQuery<
    GetInspectionInstancesQuery,
    GetInspectionInstancesQueryVariables
  >(getInspectionInstancesQuery, {
    variables: {
      channelId: params.channelId,
      search: {
        status: params.status,
        assignedTo: params.assignedTo,
        templateNameSearch: params.templateNameSearch,
      },
      pagination: {
        start: params.start ?? 0,
        perPage: params.perPage ?? DEFAULT_PER_PAGE,
      },
    },
    fetchPolicy: 'cache-and-network', 
    notifyOnNetworkStatusChange: true, 
  });

  const refetch = useCallback(
    async (overrides?: Partial<UseGetInspectionInstancesParams>) => {
      await apolloRefetch({
        channelId: overrides?.channelId ?? params.channelId,
        search: {
          status: overrides?.status ?? params.status,
          assignedTo: overrides?.assignedTo ?? params.assignedTo,
          templateNameSearch: overrides?.templateNameSearch ?? params.templateNameSearch,
        },
        pagination: {
          start: overrides?.start ?? params.start ?? 0,
          perPage: overrides?.perPage ?? params.perPage ?? DEFAULT_PER_PAGE,
        },
      });
    },
    [
      apolloRefetch,
      params.channelId,
      params.status,
      params.assignedTo,
      params.templateNameSearch,
      params.start,
      params.perPage,
    ]
  );

  const fetchMore = useCallback(async () => {
    const pageInfo = data?.inspectionInstances.pageInfo;

    if (!pageInfo) {
      return;
    }

    if (!calculateHasNextPage(pageInfo)) {
      return;
    }

    const nextStart = pageInfo.start + pageInfo.perPage;

    await apolloFetchMore({
      variables: {
        pagination: {
          start: nextStart,
          perPage: pageInfo.perPage,
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        return {
          inspectionInstances: {
            ...fetchMoreResult.inspectionInstances,
            inspectionInstances: [
              ...previousResult.inspectionInstances.inspectionInstances,
              ...fetchMoreResult.inspectionInstances.inspectionInstances,
            ],
          },
        };
      },
    });
  }, [apolloFetchMore]);

  const fetchingMore = networkStatus === NetworkStatus.fetchMore;

  const pageInfo = data?.inspectionInstances.pageInfo ?? {
    start: 0,
    total: 0,
    perPage: params.perPage ?? DEFAULT_PER_PAGE,
  };

  const hasNextPage = calculateHasNextPage(pageInfo);

  return {
    instances: data?.inspectionInstances.inspectionInstances ?? [],
    loading,
    error,
    refetch,
    fetchMore,
    hasNextPage,
    totalCount: data?.inspectionInstances.pageInfo.total ?? 0,
    pageInfo,
    fetchingMore,
  };
}
